// © 2013 - 2024 Tinder, Inc., ALL RIGHTS RESERVED

import { css } from '@linaria/core';

import { themeCssVar as globalTheme } from '../../utils/theme';

import { TEXT_FIELD_INPUT_QUIET_PROPS } from './constants';

export const theme = {
  ...TEXT_FIELD_INPUT_QUIET_PROPS,
  label: globalTheme.color.textTextFieldQuietLabelOptional,
  height: globalTheme.sizing.dimensionXlarge,
  radius: '0.6rem',
  borderWidth: globalTheme.sizing.borderWidthThin,
  paddingHorizontal: '1.4rem',
  paddingVertical: globalTheme.sizing.paddingMedium,
  labelMargin: globalTheme.sizing.paddingXsmall,
  fontFamily: globalTheme.fontSize.body1Regular,
  fontSize: globalTheme.fontSize.body1Regular,
  fontWeight: globalTheme.fontWeight.body1Regular,
  lineHeight: globalTheme.lineHeight.body1Regular,
  letterSpacing: globalTheme.letterSpacing.body1Regular,
  textTransform: globalTheme.letterCasing.body1Regular,
};

const cssVariable = {
  background: `--tui-text-field-background`,
  backgroundDisabled: `--tui-text-field-background-disabled`,
  border: `--tui-text-field-border`,
  borderDisabled: `--tui-text-field-border-disabled`,
  borderFocus: `--tui-text-field-border-focus`,
  borderError: `--tui-text-field-border-error`,
  text: `--tui-text-field-text`,
  textDisabled: `--tui-text-field-text-disabled`,
  textPlaceholder: `--tui-text-field-text-placeholder`,
  textPlaceholderDisabled: `--tui-text-field-text-placeholder-disabled`,
  icon: `--tui-text-field-icon`,
  iconDisabled: `--tui-text-field-icon-disabled`,
  focus: `--tui-text-field-focus`,
  label: `--tui-text-field-label`,
  labelError: `--tui-text-field-label-error`,
};

const container = css`
  /* Block */
  display: flex;
  position: relative;
  align-items: center;
  cursor: text;

  color: var(${cssVariable.text});
  background-color: var(${cssVariable.background});
  border-radius: ${theme.radius};
  border-color: var(${cssVariable.border});

  border-width: ${theme.borderWidth};
  border-style: solid;

  /* Variables */
  ${`${cssVariable.background}: ${theme.background};`}
  ${`${cssVariable.border}: ${theme.border};`}
  ${`${cssVariable.text}: ${theme.text};`}
  ${`${cssVariable.textPlaceholder}: ${theme.textPlaceholder};`}
  ${`${cssVariable.focus}: ${theme.focus};`}
  ${`${cssVariable.backgroundDisabled}: ${theme.backgroundDisabled};`}
  ${`${cssVariable.borderDisabled}: ${theme.borderDisabled};`}
  ${`${cssVariable.borderFocus}: ${theme.borderFocus};`}
  ${`${cssVariable.borderError}: ${theme.borderError};`}
  ${`${cssVariable.textDisabled}: ${theme.textDisabled};`}
  ${`${cssVariable.textPlaceholderDisabled}: ${theme.textPlaceholderDisabled};`}

  /* Modifiers */
  &[data-disabled] {
    cursor: default;

    color: var(${cssVariable.textDisabled});
    background-color: var(${cssVariable.backgroundDisabled});
    border-color: var(${cssVariable.borderDisabled});
  }

  &[data-error] {
    border-color: var(${cssVariable.borderError});
  }

  &[data-focused] {
    border-color: var(${cssVariable.borderFocus});
  }
`;

const startWrapper = css`
  position: relative;
  flex-basis: content;
`;

const inputWrapper = css`
  position: relative;
  flex-grow: 1;
  flex-basis: fill;
`;

const endWrapper = css`
  position: relative;
  flex-basis: content;
`;

const input = css`
  display: block;
  width: 100%;
  background: transparent;
  border: 0;
  color: inherit;
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize};
  font-weight: ${theme.fontWeight};
  line-height: ${theme.lineHeight};
  letter-spacing: ${theme.letterSpacing};
  text-transform: ${theme.textTransform};
  padding: calc(${theme.paddingVertical} - ${theme.borderWidth})
    calc(${theme.paddingHorizontal} - ${theme.borderWidth});

  &:focus {
    outline: 0;
  }

  &::placeholder {
    color: var(${cssVariable.textPlaceholder});
  }

  textarea& {
    resize: none;
  }

  /* Modifiers */
  .${container}[data-disabled] &::placeholder {
    color: var(${cssVariable.textPlaceholderDisabled});
  }
`;

const focus = css`
  display: block;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
  overflow: hidden;
  border-radius: ${theme.radius};

  opacity: 0;
  margin: -2px;
  box-shadow: 0 0 0 4px var(${cssVariable.focus});

  .${container}[data-a11y-focused] & {
    opacity: 1;
  }
`;

const icon = css`
  color: var(${cssVariable.icon});

  .${container}[data-disabled] {
    color: var(${cssVariable.iconDisabled});
  }
`;

const label = css`
  /* Block */
  display: block;
  color: var(${cssVariable.label});
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize};
  font-weight: ${theme.fontWeight};
  line-height: ${theme.lineHeight};
  letter-spacing: ${theme.letterSpacing};
  text-transform: ${theme.textTransform};
  padding-bottom: ${theme.labelMargin};

  &[data-error] {
    color: var(${cssVariable.labelError});
  }

  /* Variables */
  ${`${cssVariable.iconDisabled}: ${theme.iconDisabled};`}
`;

const styles = {
  theme,
  container,
  input,
  startWrapper,
  inputWrapper,
  endWrapper,
  focus,
  icon,
  label,
};

export default styles;
